import { useAccessContext } from '../AccessProvider'
import {
  AccessAccount,
  AccessTeam,
  ContributorType,
  UserAccess,
} from '../types'
import { CONTRIBUTOR_RIGHTS, teamToContributor } from '../utils'

const useContributorActions = (
  type: ContributorType,
  resourceOwner: AccessAccount
) => {
  const { userContributors, teamContributors, onChange } = useAccessContext()

  const viewPremission = CONTRIBUTOR_RIGHTS().VIEWER.value

  const handleAddUser = (
    user: AccessAccount,
    access: UserAccess = viewPremission
  ) => {
    onChange({
      userContributors: [...userContributors, { account: user, access }],
    })
  }

  const handleAddTeam = (
    _: AccessTeam[],
    { dataItem }: { dataItem: AccessTeam }
  ) => {
    const teamContributor = teamToContributor(dataItem, resourceOwner)

    onChange({
      teamContributors: [...teamContributors, teamContributor],
    })
  }

  const handleUserPermissionChange = ({
    value,
    userId,
    teamId,
  }: {
    value: UserAccess
    userId: string
    teamId?: string
  }) => {
    if (type === 'User') {
      const contributor = userContributors.find(
        (c) => c.account.id === userId
      )
      if (contributor?.locked) return

      const editedContributors = userContributors.map((contributor) =>
        contributor.account.id === userId
          ? { ...contributor, access: value }
          : contributor
      )

      onChange({
        userContributors: editedContributors,
      })

      return
    }

    if (type === 'Team') {
      const updatedTeams = teamContributors.map(({ team, contributors }) => {
        if (team.id === teamId) {
          return {
            team,
            contributors: contributors.map((user) =>
              user.account.id === userId && !user.locked
                ? { ...user, access: value }
                : user
            ),
          }
        }
        return { team, contributors }
      })

      onChange({
        teamContributors: updatedTeams,
      })

      return
    }
  }

  const handleUserDelete = ({
    userId,
    teamId,
  }: {
    userId: string
    teamId?: string
  }) => {
    if (type === 'User') {
      const contributor = userContributors.find(
        (c) => c.account.id === userId
      )
      if (contributor?.removable === false) return

      const filteredContributors = userContributors.filter(
        ({ account }) => account.id !== userId
      )

      onChange({
        userContributors: filteredContributors,
      })

      return
    }

    if (type === 'Team') {
      const team = teamContributors.find((tc) => tc.team.id === teamId)
      const contributor = team?.contributors.find(
        (c) => c.account.id === userId
      )
      if (contributor?.removable === false) return

      const updatedTeams = teamContributors.map((teamContributor) =>
        teamContributor.team.id === teamId
          ? {
              ...teamContributor,
              contributors: teamContributor.contributors.filter(
                ({ account }) => account.id !== userId
              ),
            }
          : teamContributor
      )

      onChange({
        teamContributors: updatedTeams,
      })

      return
    }
  }

  const handleTeamDelete = (teamId: string) => {
    const filteredTeams = teamContributors.filter(
      ({ team }) => team.id !== teamId
    )

    onChange({
      teamContributors: filteredTeams,
    })
  }

  const handleTeamMembersAccessChange = (
    teamId: string,
    access: UserAccess
  ) => {
    const updatedTeams = teamContributors.map((teamContributor) => {
      if (teamContributor.team.id === teamId) {
        return {
          ...teamContributor,
          contributors: teamContributor.contributors.map((user) =>
            user.locked ? user : { ...user, access }
          ),
        }
      }
      return teamContributor
    })

    onChange({
      teamContributors: updatedTeams,
    })
  }

  return {
    handleAddUser,
    handleAddTeam,
    onContributorDelete: handleUserDelete,
    onPermissionChange: handleUserPermissionChange,
    onTeamDelete: handleTeamDelete,
    onTeamMembersAccessChange: handleTeamMembersAccessChange,
  }
}

export default useContributorActions
